import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import CreatorModePanelWrapper from "../../../../components/CreatorDashboard/CreatorModePanelWrapper";
import { Colors } from "../../../../utils/colors";
import { TabPanel } from "../../../../components/FullEventSubComponents/TabPanel";
import EmptyStateButton from "../../../../components/Buttons/EmptyStateButton";
import {
  connectToStripeAccount,
  getStripeAccountBalance,
  testStripe,
} from "../../../../utils/stripeAccountUtils";
import { Icon } from "@iconify/react";
import StandardBorderedContainer from "../../../../components/Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../../../../components/Dividers/HorizontalDivider";
import { FormatCurrency } from "../../../../utils/FormatCurrency";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import RectangleButton from "../../../../components/Buttons/RectangleButton";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "../../../../components/DropdownMenu";
import IOSSwitch from "../../../../components/IOSSwitch";
import Skeleton from "react-loading-skeleton";
import AddStripeFundsModal from "../../../../components/Stripe/AddStripeFundsModal";
import EnablePaidTicketsModal from "../../../../components/DisplayModal/EnablePaidTicketsModal";
import { API } from "../../../../API";
import {
  StripeAccountStatus,
  StripePayoutSchedule,
} from "@markit/common.types";
import { capitalizeFirstLetter } from "@markit/common.utils";
import TextHoverButton from "../../../../components/Buttons/TextHoverButton";
import SendManualPayoutModal from "../../../../components/Stripe/SendManualPayoutModal";
import DropdownArrowIcon from "../../../../components/DropdownsAndTabs/DropdownArrowIcon";
import DisputesPanel from "../../../../components/Stripe/DisputesPanel";

const PayoutsPanel = () => {
  const { account } = useSelector(getAccountState);
  const { accountData, readyForPayment } = account;
  const [tabValue, setTabValue] = useState(0);
  const [stripeBalance, setStripeBalance] = useState<number | null>(null); // in cents
  const [availableStripeBalance, setAvailableStripeBalance] = useState<
    number | null
  >(null); // in cents
  const [payoutsPaused, setPayoutsPaused] = useState(false);
  const [loadingManageStripe, setLoadingManageStripe] = useState(false);
  const [stripeVisible, setStripeVisible] = useState(false);
  const [loadingPayoutSchedule, setLoadingPayoutSchedule] = useState(false);
  const [loadingPausePayouts, setLoadingPausePayouts] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [addFundsModal, setAddFundsModal] = useState(false);
  const [manualPayoutModal, setManualPayoutModal] = useState(false);

  const styles = {
    mainContainer: {
      padding: 30,
      height: "100%",
      backgroundColor: Colors.WHITE1,
      gap: 24,
    },
    sectionContainer: {
      flex: 1,
      height: "fit-content",
      paddingBlock: 14,
      backgroundColor: Colors.WHITE,
    },
  };

  const userStripeAccountStatus = useMemo(
    () =>
      testStripe(accountData.uid)
        ? accountData.testStripeAccountStatus || StripeAccountStatus.INCOMPLETE
        : accountData.stripeAccountStatus,
    [
      accountData.uid,
      accountData.stripeAccountStatus,
      accountData.testStripeAccountStatus,
    ]
  );

  const awaitingManualReview = useMemo(
    () => userStripeAccountStatus === StripeAccountStatus.MANUAL_REVIEW,
    [userStripeAccountStatus]
  );

  const retrieveStripeBalance = useCallback(async () => {
    setStripeBalance(null);
    setAvailableStripeBalance(null);
    const stripeAccountId = testStripe(accountData.uid)
      ? accountData.testStripeAccountId || ""
      : accountData.stripeAccountId;
    const { balance, availableBalance } = await getStripeAccountBalance(
      accountData.uid,
      stripeAccountId || ""
    );
    setStripeBalance(balance);
    setAvailableStripeBalance(availableBalance);
  }, [
    accountData.uid,
    accountData.stripeAccountId,
    accountData.testStripeAccountId,
  ]);

  useAsyncOnMount(async () => {
    await retrieveStripeBalance();
    setPayoutsPaused(accountData.stripeAccountData.payoutsPaused);
  });

  const manageStripe = useCallback(async () => {
    setLoadingManageStripe(true);
    await connectToStripeAccount(accountData.uid);
    setLoadingManageStripe(false);
  }, [accountData.uid]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean, info?: { source: "trigger" | "menu" }) => {
      if (info?.source === "trigger" || nextOpen) {
        setOpenDropdown(nextOpen);
      }
    },
    [setOpenDropdown]
  );

  const updateStripePayoutSchedule = useCallback(
    async (payoutSchedule: StripePayoutSchedule) => {
      if (accountData.stripeAccountData.payoutSchedule === payoutSchedule) {
        return;
      }
      setLoadingPayoutSchedule(true);
      await (
        testStripe(accountData.uid)
          ? API.testPayment.updateStripePayoutSchedule
          : API.payment.updateStripePayoutSchedule
      )({
        userId: accountData.uid,
        schedule: payoutSchedule,
        payoutsPaused: null,
      });
      setLoadingPayoutSchedule(false);
    },
    [accountData.stripeAccountData.payoutSchedule, accountData.uid]
  );

  const pauseAutomaticPayouts = useCallback(async () => {
    setPayoutsPaused(!payoutsPaused);
    setLoadingPausePayouts(true);
    await (
      testStripe(accountData.uid)
        ? API.testPayment.updateStripePayoutSchedule
        : API.payment.updateStripePayoutSchedule
    )({
      userId: accountData.uid,
      schedule: accountData.stripeAccountData.payoutSchedule,
      payoutsPaused: !payoutsPaused,
    });
    setLoadingPausePayouts(false);
  }, [
    accountData.stripeAccountData.payoutSchedule,
    accountData.uid,
    payoutsPaused,
  ]);

  const scheduleItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Weekly (Mondays)",
        key: "weekly_mondays",
        onPress: async () => {
          setOpenDropdown(false);
          await updateStripePayoutSchedule(StripePayoutSchedule.WEEKLY);
        },
      },
      {
        title: "Daily",
        key: "daily",
        onPress: async () => {
          setOpenDropdown(false);
          await updateStripePayoutSchedule(StripePayoutSchedule.DAILY);
        },
      },
    ],
    [updateStripePayoutSchedule, setOpenDropdown]
  );

  return (
    <CreatorModePanelWrapper
      title={"Payouts"}
      headerRight={<></>}
      headerTabs={{
        tabNames: ["Manage Stripe", "Disputes"],
        tabValue: tabValue,
        setTabValue: setTabValue,
      }}
    >
      <div className="ColumnNormal" style={styles.mainContainer}>
        <TabPanel value={tabValue} index={0}>
          {readyForPayment ? (
            <div className="RowNormal" style={{ gap: 20 }}>
              <StandardBorderedContainer
                containerStyles={styles.sectionContainer}
              >
                <div className="ColumnNormal">
                  <div
                    className="ColumnNormal"
                    style={{ gap: 4, paddingInline: 14 }}
                  >
                    <span className="sectionTitle">Stripe Balance</span>
                    <span className="smallBodySubtext">
                      View your Stripe balance and visit your Stripe dashboard
                      <br />
                      for more details and to monitor past/upcoming payouts.
                    </span>
                  </div>
                  <HorizontalDivider altMargin={14} />
                  <div
                    className="AlignedRowSpaced"
                    style={{ paddingInline: 14, gap: 14 }}
                  >
                    <div className="ColumnNormal">
                      <span
                        style={{
                          fontSize: 24,
                          fontWeight: 500,
                          color:
                            stripeBalance !== null && stripeBalance >= 0
                              ? Colors.BLACK
                              : Colors.RED3,
                        }}
                      >
                        {stripeBalance !== null ? (
                          FormatCurrency(stripeBalance / 100)
                        ) : (
                          <Skeleton width={100} />
                        )}
                      </span>
                      <div className="AlignedRow" style={{ gap: 7 }}>
                        <span className="smallBodySubtext">
                          Total Stripe Balance
                        </span>
                        <TextHoverButton
                          text="Refresh"
                          onPress={retrieveStripeBalance}
                          icon={
                            <Icon
                              icon="ion:sync-sharp"
                              height={13}
                              style={{ color: Colors.BLUE5 }}
                            />
                          }
                          textStyles={{ color: Colors.BLUE5 }}
                          disabled={stripeBalance === null}
                        />
                      </div>
                    </div>
                    <div className="AlignedRow" style={{ gap: 7 }}>
                      <RectangleButton
                        buttonLabel="Add Funds"
                        onPress={() => setAddFundsModal(true)}
                        altColor={Colors.WHITE1}
                        altTextColor={Colors.BLACK}
                        containerStyles={{
                          padding: "14px 12px",
                          border: `0.5px solid ${Colors.GRAY2}`,
                          whiteSpace: "nowrap",
                        }}
                      />
                      <RectangleButton
                        buttonLabel="Stripe Dashboard"
                        iconRight={
                          <Icon
                            icon="ion:arrow-forward"
                            height={14}
                            style={{
                              color: Colors.WHITE,
                              transform: "rotateY(0deg) rotate(-45deg)",
                            }}
                          />
                        }
                        onPress={manageStripe}
                        altColor={Colors.PURPLE11}
                        altTextColor={Colors.WHITE}
                        containerStyles={{
                          padding: "14px 12px",
                          whiteSpace: "nowrap",
                        }}
                        loading={loadingManageStripe}
                      />
                    </div>
                  </div>
                  <HorizontalDivider altMargin={14} />
                  <div
                    className="ColumnNormal"
                    style={{ gap: 4, paddingInline: 14 }}
                  >
                    <span className="sectionTitle">
                      {availableStripeBalance !== null ? (
                        FormatCurrency(availableStripeBalance / 100)
                      ) : (
                        <Skeleton width={80} />
                      )}
                    </span>
                    <span className="smallBodySubtext">
                      Available Balance for Payout: The portion of your balance
                      that is available to payout now.
                    </span>
                  </div>
                  {/* <HorizontalDivider altMargin={14} />
                  <div
                    className="ColumnNormal"
                    style={{ gap: 4, paddingInline: 14 }}
                  >
                    <span className="sectionTitle">$100.00</span>
                    <span className="smallBodySubtext">
                      Minimum Stripe Balance: We hold a set amount in your
                      Stripe balance to handle dispute charges and refunds. This
                      number may change depending on your total balance.
                    </span>
                  </div> */}
                </div>
              </StandardBorderedContainer>
              <StandardBorderedContainer
                containerStyles={styles.sectionContainer}
              >
                <div className="ColumnNormal">
                  <div
                    className="ColumnNormal"
                    style={{ gap: 4, paddingInline: 14 }}
                  >
                    <span className="sectionTitle">Stripe Payouts</span>
                    <span className="smallBodySubtext">
                      Manage your Stripe payouts and payout schedule.
                    </span>
                  </div>
                  <HorizontalDivider altMargin={14} />
                  <div
                    className="AlignedRowSpaced"
                    style={{
                      paddingInline: 14,
                      opacity: payoutsPaused ? 0.5 : 1,
                    }}
                  >
                    <div className="ColumnNormal" style={{ gap: 4 }}>
                      <span className="bodyMedium">
                        Automatic Payout Schedule
                      </span>
                      <span className="smallBodySubtext">
                        Select when you want your Stripe payouts to happen
                      </span>
                    </div>
                    <DropdownMenu
                      dropdownItems={scheduleItems}
                      trigger={["click"]}
                      placement="bottomRight"
                      isOpen={openDropdown}
                      onOpenChange={handleOpenChange}
                      disabled={payoutsPaused}
                    >
                      <RectangleButton
                        buttonLabel={
                          <span className="bodyMedium">
                            {capitalizeFirstLetter(
                              accountData.stripeAccountData.payoutSchedule
                            )}
                            {accountData.stripeAccountData.payoutSchedule ===
                            StripePayoutSchedule.WEEKLY
                              ? " (Mondays)"
                              : ""}
                          </span>
                        }
                        iconRight={
                          <DropdownArrowIcon
                            isOpen={openDropdown}
                            height={14}
                            color={Colors.BLACK}
                          />
                        }
                        onPress={() => {}}
                        altColor={Colors.WHITE1}
                        altTextColor={Colors.BLACK}
                        containerStyles={{
                          padding: "14px 12px",
                          border: `0.5px solid ${Colors.GRAY2}`,
                          whiteSpace: "nowrap",
                          pointerEvents: payoutsPaused ? "none" : "all",
                        }}
                        loading={loadingPayoutSchedule}
                      />
                    </DropdownMenu>
                  </div>
                  <HorizontalDivider altMargin={14} />

                  <div
                    className="AlignedRowSpaced"
                    style={{ paddingInline: 14 }}
                  >
                    <div className="ColumnNormal" style={{ gap: 4 }}>
                      <span className="bodyMedium">
                        Pause Automatic Payouts
                      </span>
                      <span className="smallBodySubtext">
                        Pause your automatic Stripe payouts and manually send
                        payouts.
                        <br />
                        We recommend this option if you frequently issue
                        refunds.
                      </span>
                    </div>
                    <IOSSwitch
                      sx={{ m: 1 }}
                      checked={payoutsPaused}
                      onChange={pauseAutomaticPayouts}
                      style={{ cursor: "pointer" }}
                      switchColor={Colors.BLUE5}
                      disabled={loadingPausePayouts}
                    />
                  </div>
                  <HorizontalDivider altMargin={14} />
                  <div
                    className="AlignedRowSpaced"
                    style={{ paddingInline: 14, gap: 24 }}
                  >
                    <div className="ColumnNormal" style={{ gap: 4 }}>
                      <span className="bodyMedium">Initiate Manual Payout</span>
                      <span className="smallBodySubtext">
                        Initiate a manual payout if you are awaiting auto payout
                        or if manually paused. After initiating a payout, it may
                        take a few days to process.
                      </span>
                    </div>
                    <div>
                      <RectangleButton
                        buttonLabel="Manual Payout"
                        onPress={() => setManualPayoutModal(true)}
                        altColor={Colors.BLACK}
                        altTextColor={Colors.WHITE}
                        containerStyles={{
                          padding: "14px 12px",
                          whiteSpace: "nowrap",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </StandardBorderedContainer>
            </div>
          ) : (
            <div className="Centering" style={{ minHeight: "50vh" }}>
              <div className="ColumnCentering">
                <EmptyStateButton
                  title={
                    awaitingManualReview
                      ? "Awaiting Manual Review from our Team"
                      : "Connect to Stripe"
                  }
                  description="Start using paid products and services on Markit by connecting your Stripe account."
                  icon={
                    <Icon
                      icon={"fa:cc-stripe"}
                      height={38}
                      style={{ color: Colors.PURPLE11 }}
                    />
                  }
                  iconBox={80}
                />
                <RectangleButton
                  buttonLabel="Connect"
                  onPress={() => setStripeVisible(true)}
                  disabled={awaitingManualReview}
                  altColor={Colors.BLACK}
                  altTextColor={Colors.WHITE}
                  width={100}
                  containerStyles={{
                    borderRadius: 100,
                    padding: "14px 12px",
                  }}
                />
              </div>
            </div>
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <DisputesPanel />
        </TabPanel>
      </div>
      {addFundsModal ? (
        <AddStripeFundsModal
          isVisible={addFundsModal}
          setIsVisible={setAddFundsModal}
        />
      ) : null}
      {manualPayoutModal ? (
        <SendManualPayoutModal
          isVisible={manualPayoutModal}
          setIsVisible={setManualPayoutModal}
          availableBalance={availableStripeBalance || 0}
          updateBalance={(subtractAmount: number) => {
            setStripeBalance((prev) => (prev ? prev - subtractAmount : null));
            setAvailableStripeBalance((prev) =>
              prev ? prev - subtractAmount : null
            );
          }}
        />
      ) : null}
      {stripeVisible ? (
        <EnablePaidTicketsModal
          isVisible={stripeVisible}
          setIsVisible={setStripeVisible}
        />
      ) : null}
    </CreatorModePanelWrapper>
  );
};

export default PayoutsPanel;
